import { urlParts } from '~~/common/utils/urlParts';

/**
 * Utilities for handling URLs containing consecutive slashes.
 *
 * The server middleware (shop/server/middleware/legacyRedirects.ts) 301s every
 * URL containing consecutive slashes to its single-slash form, while the app
 * deterministically rebuilds the canonical URL — which may contain an empty
 * segment (e.g. "/shop/drinks//gin.c-6501" when a category name is missing) —
 * and redirects back, ping-ponging forever. Such redirects must be detected
 * with wouldCauseRedirectLoop and aborted instead of being followed.
 */

/**
 * Collapses consecutive slashes in the given URL to single slashes.
 * E.g. "/shop//page" -> "/shop/page".
 */
export function fixDoubleSlash (url: string): string {
  return url.replace(/\/{2,}/g, '/');
}

/**
 * Returns true if redirecting to the given target URL would cause an infinite
 * redirect loop, because the server middleware would 301 it right back to its
 * single-slash form. The target is resolved with urlParts against our own
 * host, so absolute URLs pointing back at the app (e.g.
 * "https://ourhost/shop//page") are checked too; outer URLs never pass
 * through our middleware and therefore cannot loop with it.
 */
export function wouldCauseRedirectLoop (hostString: string, targetUrl: string): boolean {
  const { isOuter, link } = urlParts(hostString, targetUrl);
  if (isOuter) {
    return false;
  }
  return link !== fixDoubleSlash(link);
}
